import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import {
  normalizePublicBookingStatsPayload,
  resolveSocialProofBookingDisplay,
  type PublicBookingStatsPayload,
  type SocialProofBookingDisplay,
} from '../utils/publicBookingStats';

const CACHE_DURATION_MS = 5 * 60 * 1000;

let cached: { data: PublicBookingStatsPayload; ts: number } | null = null;
let inflight: Promise<PublicBookingStatsPayload | null> | null = null;

async function fetchPublicBookingStats(): Promise<PublicBookingStatsPayload | null> {
  const now = Date.now();
  if (cached && now - cached.ts < CACHE_DURATION_MS) {
    return cached.data;
  }
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const { data, error } = await supabase.rpc('get_public_booking_stats');
      if (error) return null;
      const normalized = normalizePublicBookingStatsPayload(data);
      if (!normalized) return null;
      cached = { data: normalized, ts: Date.now() };
      return normalized;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/**
 * Homepage social-proof booking counts.
 * On fetch failure: display stays null (trust main line still shows; never fake 0).
 */
export function usePublicBookingStats() {
  const [display, setDisplay] = useState<SocialProofBookingDisplay | null>(() => {
    if (cached && Date.now() - cached.ts < CACHE_DURATION_MS) {
      return resolveSocialProofBookingDisplay(cached.data);
    }
    return null;
  });
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const hadCache = Boolean(cached && Date.now() - (cached?.ts ?? 0) < CACHE_DURATION_MS);
      if (!hadCache) setLoading(true);

      const stats = await fetchPublicBookingStats();
      if (cancelled) return;

      setDisplay(resolveSocialProofBookingDisplay(stats));
      setLoading(false);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return { display, loading };
}

/** Test helper — clear module cache between tests. */
export function __resetPublicBookingStatsCacheForTests() {
  cached = null;
  inflight = null;
}
